import {container, inject, singleton} from "tsyringe";
import {ProxyService} from "../../advanced-form/proxy.service";
import {Issue} from "../../domain/entities/issue.entity";
import {EntityManager} from "../../persistence/entity.manager";
import {IntervalLoadingGuard} from "../../services/interval-loading.guard";
import {AbstractBaseListener} from "../abstract-base.listener";
import {BaseMessage} from "../base-message.interface";
import {OnMessage} from "../on-message.decorator";
import {EntityCreatedPayload} from "../payloads/entity-created.payload";
import {EntityPersistedPayload} from "../payloads/entity-persisted.payload";
import {IssueIntervalChangedPayload} from "../payloads/issue-interval-changed.payload";
import {Entity} from "../../domain/entities/entity";
import {Project} from "../../domain/entities/project.entity";
import {IssueRepository} from "../../domain/repositories/issue.repository";

@singleton()
export class EntityCreatedListener extends AbstractBaseListener {

    constructor(
        @inject(EntityManager) private readonly _entityManager: EntityManager,
        @inject(ProxyService) private readonly _proxyService: ProxyService<any>
    ) {
        super();
    }

    @OnMessage(`entity.created`, (message: BaseMessage<EntityCreatedPayload<Entity>>) => message.payload.prototype !== Project)
    public async onNonProjectEntityCreated(messages: BaseMessage<EntityCreatedPayload<Entity>>[]): Promise<void> {

        for (const message of messages) {
            const {uid, entity, prototype} = message.payload;

            // Mark entity as persisted in EntityManager
            this._entityManager.markAsPersisted(entity);

            // Publish entity.persisted event for UI components
            // Entity is now guaranteed to have correct persistence state (isNew() = false)
            this._messageBroker.publish<EntityPersistedPayload<Entity>>({
                type: 'entity.persisted',
                identityHash: uid,
                payload: { uid, entity, prototype }
            });

            // **LEGACY SYSTEM** - Only non-Issue entities still use ProxyService form commits
            if (!(entity instanceof Issue)) {
                if (!this._proxyService.has(uid)) {
                    // No form registered for this entity — skip THIS entity, not the rest of
                    // the batch. Messages arrive batched (@OnMessage buffers 250ms), so a
                    // `return` here abandons every message queued behind this one: those
                    // entities never get marked as persisted, keep reporting isNew() === true,
                    // and a later save re-creates them as duplicates.
                    continue;
                }

                const form = await this._proxyService.get(uid)!;
                if (form) {
                    form.commit();
                }
            }

            if (entity instanceof Issue) {
                const interval = entity.timeRange;
                container.resolve(IntervalLoadingGuard).removeRange(interval);
                this._messageBroker.publish<IssueIntervalChangedPayload>({
                    type: `issue.interval.changed`,
                    identityHash: interval.toISO(),
                    payload: {interval}
                });
            }

        }
    }

    @OnMessage(`entity.created`, (message: BaseMessage<EntityCreatedPayload<Entity>>) => message.payload.prototype === Project)
    public async onProjectEntityCreated(messages: BaseMessage<EntityCreatedPayload<Project>>[]): Promise<void> {

        for (const message of messages) {
            const {uid, entity: project, prototype} = message.payload;

            // Mark entity as persisted in EntityManager
            this._entityManager.markAsPersisted(project);

            // Publish entity.persisted event for UI components
            this._messageBroker.publish<EntityPersistedPayload<Project>>({
                type: 'entity.persisted',
                identityHash: uid,
                payload: { uid, entity: project, prototype }
            });

            project.commit();
            const formId = `${project.uid}-${project.type.key}`;
            if (!this._proxyService.has(formId)) {
                // Same batch-abort hazard as in onNonProjectEntityCreated above — skip the
                // entity, not the batch.
                continue;
            }
            const form = await this._proxyService.get(formId)!;
            if (form) {
                form.commit();
            }
        }
    }

    @OnMessage(`entity.created`, (message: BaseMessage<EntityCreatedPayload<Issue>>) => message.payload.prototype === Issue && !!message.payload.entity.project && container.resolve(EntityManager).isLoaded(message.payload.entity.project!.uid))
    public async onMilestoneCreated(messages: BaseMessage<EntityCreatedPayload<Issue>>[]): Promise<void> {

        for (const message of messages) {
            const issue = message.payload.entity;
            const project = issue.project!;
            project.milestones = container.resolve(IssueRepository).findAllMilestonesByProject(project.uid);
        }

    }
}